import { useState } from 'react';
import { Shield, KeyRound, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import type { MfaChallengeState } from '../lib/auth-context';

type Props = {
  challenge: MfaChallengeState;
  completeMfaTotp: (code: string) => Promise<void>;
  completeMfaPasskey: () => Promise<void>;
  cancelMfaChallenge: () => Promise<void>;
  onSuccess: () => void;
};

export function MfaChallengePanel({
  challenge,
  completeMfaTotp,
  completeMfaPasskey,
  cancelMfaChallenge,
  onSuccess,
}: Props) {
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleTotp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await completeMfaTotp(code.trim());
      onSuccess();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Verification failed');
    } finally {
      setBusy(false);
    }
  };

  const handlePasskey = async () => {
    setError(null);
    setBusy(true);
    try {
      await completeMfaPasskey();
      onSuccess();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Passkey verification failed');
    } finally {
      setBusy(false);
    }
  };

  const handleCancel = async () => {
    setBusy(true);
    try {
      await cancelMfaChallenge();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-full bg-violet-100 dark:bg-violet-950 flex items-center justify-center flex-shrink-0">
          <Shield className="w-5 h-5 text-violet-600 dark:text-violet-400" />
        </div>
        <div className="space-y-1 min-w-0">
          <h2 className="text-lg font-medium text-neutral-900 dark:text-neutral-100">
            Two-factor verification
          </h2>
          <p className="text-sm text-neutral-600 dark:text-neutral-400">
            {challenge.factorType === 'totp'
              ? 'Enter the 6-digit code from your authenticator app.'
              : 'Use your passkey or security key to finish signing in.'}
          </p>
        </div>
      </div>

      {error && (
        <p className="text-sm text-red-600 dark:text-red-400" role="alert">
          {error}
        </p>
      )}

      {challenge.factorType === 'totp' ? (
        <form onSubmit={handleTotp} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="mfa-code">Authenticator code</Label>
            <Input
              id="mfa-code"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9]*"
              maxLength={12}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\s/g, ''))}
              placeholder="000000"
              className="font-mono tracking-widest text-center text-lg"
              disabled={busy}
              autoFocus
            />
          </div>
          <Button type="submit" className="w-full" disabled={busy || code.replace(/\s/g, '').length < 6}>
            {busy ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Verifying…
              </>
            ) : (
              'Verify and continue'
            )}
          </Button>
        </form>
      ) : (
        <div className="space-y-3">
          <Button type="button" className="w-full" onClick={handlePasskey} disabled={busy}>
            {busy ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Waiting for passkey…
              </>
            ) : (
              <>
                <KeyRound className="w-4 h-4 mr-2" />
                Continue with passkey
              </>
            )}
          </Button>
        </div>
      )}

      <Button type="button" variant="ghost" className="w-full text-neutral-500" onClick={handleCancel} disabled={busy}>
        Cancel and sign out
      </Button>
    </div>
  );
}
